import * as Sentry from "@sentry/browser";
import { useCallback, useEffect, useReducer, useRef } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";

// A group or membership that has been created locally but whose POST has not answered yet carries
// one of these ids instead of a server id. Nothing may be sent back to the API addressed by one.
const OPTIMISTIC_ID_PREFIX = "optimistic-";

export function isOptimisticId(id) {
  return String(id).startsWith(OPTIMISTIC_ID_PREFIX);
}

export const LOAD_TIMEOUT_MS = 15000;

// Group names are unique per profile, case-insensitively (Interact::FavoriteGroup).
export function findGroupByName(groups, name) {
  const wanted = name.trim().toLowerCase();
  return groups.find((g) => g.name.trim().toLowerCase() === wanted);
}

// Never rejects; any failure comes back as { error }.
async function readWithTimeout(read) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
  try {
    return await read({ signal: controller.signal });
  } catch (e) {
    return { error: { status: null, statusText: controller.signal.aborted ? "timed out" : String(e?.message || e) } };
  } finally {
    clearTimeout(timer);
  }
}

function reportLoadFailure(source, error) {
  const message = `Saved groups: couldn't load ${source} (${error.status ?? error.statusText})`;
  qlog.error(message, error);
  Sentry.captureMessage(message, "warning");
}

// "Name has already been taken" from the API's { errors: [{ source, detail }] } body.
function describeApiError(error) {
  const details = (error?.body?.errors || [])
    .map(({ source, detail }) => {
      if (!detail) return null;
      if (!source) return detail;
      return `${source.charAt(0).toUpperCase()}${source.slice(1)} ${detail}`;
    })
    .filter(Boolean);
  return details.length > 0 ? details.join(". ") : undefined;
}

const initialState = {
  groups: [],
  // { [sourceable_type]: { [sourceable_id]: [{ id: membershipId, saved_group_id }] } }
  membershipIndex: {},
  isLoading: true,
  error: null,
};

// The favorites_index view names its columns after the model layer (favoriteable_*,
// favorite_group_id); the in-memory index keeps the source_directory naming. This is the one
// place the two meet.
function buildMembershipIndex(rows) {
  return rows.reduce((acc, row) => {
    const type = row.favoriteable_type;
    const sid = row.favoriteable_id;
    return {
      ...acc,
      [type]: {
        ...(acc[type] || {}),
        [sid]: [
          ...(acc[type]?.[sid] || []),
          {
            id: row.id,
            saved_group_id: row.favorite_group_id,
            name: row.favoriteable_name,
            imageUrl: row.favoriteable_image_url,
            slug: row.favoriteable_slug,
          },
        ],
      },
    };
  }, {});
}

const STORY_IDEA_TYPE = "Enrich::PublicPitch";

// V1-COMPAT: only V1 bylines link by migrate_id, so resolve profile_ids to V1 PrUser ids only when
// V1-hosted (V2_URL set); V2 links by slug and skips this round-trip.
async function resolveAuthorMigrateIds(pitches, isCancelled) {
  if (!globalThis.Qwoted?.V2_URL) return {};

  const profileIds = [...new Set(pitches.map((pitch) => pitch.profile_id).filter(Boolean))];
  if (profileIds.length === 0) return {};

  const { data, error } = await qapi.userProfiles.getMigrateIdsByProfileIds(profileIds).catch((e) => ({ error: e }));
  if (error || isCancelled() || !Array.isArray(data)) return {};

  return data.reduce((acc, row) => ({ ...acc, [row.profile_id]: row.migrate_id }), {});
}

// favorites_index returns a null name for saved story ideas (its view can't join the downstream
// pitch table), so their titles come from the enrich public_pitches API in this follow-up read.
async function hydratePublicPitchNames(membershipRows, isCancelled, dispatch) {
  const ids = [
    ...new Set(
      membershipRows
        .filter((row) => row.favoriteable_type === STORY_IDEA_TYPE && !row.favoriteable_name)
        .map((row) => row.favoriteable_id),
    ),
  ];
  if (ids.length === 0) return;

  const { data, error } = await qapi.publicPitches.fetchTitlesByIds(ids).catch((e) => ({ error: e }));
  if (error || isCancelled() || !Array.isArray(data) || data.length === 0) return;

  const migrateIdByProfileId = await resolveAuthorMigrateIds(data, isCancelled);

  const detailsById = data.reduce(
    (acc, pitch) => ({
      ...acc,
      [pitch.id]: {
        title: pitch.title,
        authorName: pitch.profile_name,
        authorSlug: pitch.profile_slug,
        authorMigrateId: migrateIdByProfileId[pitch.profile_id],
      },
    }),
    {},
  );
  dispatch({ type: "HYDRATE_PUBLIC_PITCH_NAMES", detailsById });
}

function applyPublicPitchNames(membershipIndex, detailsById) {
  const byId = membershipIndex[STORY_IDEA_TYPE];
  if (!byId) return membershipIndex;

  const patched = Object.fromEntries(
    Object.entries(byId).map(([sid, memberships]) => {
      const details = detailsById[sid];
      if (!details) return [sid, memberships];
      return [
        sid,
        memberships.map((m) => ({
          ...m,
          name: m.name || details.title,
          authorName: m.authorName || details.authorName,
          authorSlug: m.authorSlug || details.authorSlug,
          authorMigrateId: m.authorMigrateId || details.authorMigrateId,
        })),
      ];
    }),
  );
  return { ...membershipIndex, [STORY_IDEA_TYPE]: patched };
}

function reducer(state, action) {
  switch (action.type) {
    case "LOADED":
      return {
        ...state,
        groups: action.groups,
        membershipIndex: buildMembershipIndex(action.memberships),
        isLoading: false,
        error: action.error || null,
      };
    case "HYDRATE_PUBLIC_PITCH_NAMES":
      return {
        ...state,
        membershipIndex: applyPublicPitchNames(state.membershipIndex, action.detailsById),
      };
    case "ERROR":
      return { ...state, isLoading: false, error: action.error };
    case "REPLACE_STATE":
      return { ...state, ...action.patch };
    case "INCREMENT_MEMBER_COUNT":
      return {
        ...state,
        groups: state.groups.map((g) =>
          g.id === action.groupId ? { ...g, member_count: (g.member_count || 0) + 1 } : g,
        ),
      };
    case "DECREMENT_MEMBER_COUNT":
      return {
        ...state,
        groups: state.groups.map((g) =>
          g.id === action.groupId ? { ...g, member_count: Math.max(0, (g.member_count || 0) - 1) } : g,
        ),
      };
    default:
      return state;
  }
}

export default function useSavedGroups() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const unmountedRef = useRef(false);

  const load = useCallback(async () => {
    if (!globalThis.Qwoted?.CURRENT_USER_ID) {
      dispatch({ type: "LOADED", groups: [], memberships: [] });
      return;
    }

    const [groupsResult, membershipsResult] = await Promise.all([
      readWithTimeout(qapi.favoriteGroups.listGroups),
      readWithTimeout(qapi.favoriteGroups.fetchMembershipIndex),
    ]);

    if (unmountedRef.current) return;

    if (groupsResult.error) {
      reportLoadFailure("groups", groupsResult.error);
      dispatch({ type: "ERROR", error: { source: "groups", ...groupsResult.error } });
      return;
    }

    if (membershipsResult.error) reportLoadFailure("saved sources", membershipsResult.error);
    const memberships = membershipsResult.error ? [] : membershipsResult.data || [];

    dispatch({
      type: "LOADED",
      groups: groupsResult.data || [],
      memberships,
      error: membershipsResult.error ? { source: "memberships", ...membershipsResult.error } : null,
    });

    await hydratePublicPitchNames(memberships, () => unmountedRef.current, dispatch);
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    load();
    return () => {
      unmountedRef.current = true;
    };
  }, [load]);

  const createGroup = useCallback(async (name) => {
    const prev = stateRef.current;
    const existing = findGroupByName(prev.groups, name);
    if (existing) return isOptimisticId(existing.id) ? null : existing;

    const optimisticGroup = { id: `${OPTIMISTIC_ID_PREFIX}${Date.now()}`, name, member_count: 0 };
    dispatch({
      type: "REPLACE_STATE",
      patch: { groups: [...prev.groups, optimisticGroup].sort((a, b) => a.name.localeCompare(b.name)) },
    });

    const { data, error } = await qapi.favoriteGroups.createGroup({ name }).catch((e) => ({ error: e }));
    if (error) {
      dispatch({
        type: "REPLACE_STATE",
        patch: { groups: stateRef.current.groups.filter((g) => String(g.id) !== String(optimisticGroup.id)) },
      });
      globalThis.Qwoted?.showToast?.({
        toastTitle: "Couldn't create group",
        toastBody: describeApiError(error),
        variant: "danger",
      });
      return null;
    }
    const newGroup = data?.data?.attributes || data;
    dispatch({
      type: "REPLACE_STATE",
      patch: {
        groups: [...stateRef.current.groups.filter((g) => String(g.id) !== String(optimisticGroup.id)), newGroup].sort(
          (a, b) => a.name.localeCompare(b.name),
        ),
      },
    });
    return newGroup;
  }, []);

  const addToGroup = useCallback(async (groupId, sourceableType, sourceableId, meta = {}) => {
    const prev = stateRef.current;

    const type = sourceableType;
    const id = sourceableId;
    const sourceMeta = { name: meta.name, imageUrl: meta.imageUrl, slug: meta.slug };
    const prevMemberships = prev.membershipIndex[type]?.[id] || [];
    const optimisticMembership = { id: `${OPTIMISTIC_ID_PREFIX}${Date.now()}`, saved_group_id: groupId, ...sourceMeta };
    const newIndex = {
      ...prev.membershipIndex,
      [type]: {
        ...prev.membershipIndex[type],
        [id]: [...prevMemberships, optimisticMembership],
      },
    };
    dispatch({ type: "REPLACE_STATE", patch: { membershipIndex: newIndex } });
    dispatch({ type: "INCREMENT_MEMBER_COUNT", groupId });

    const { data, error } = await qapi.favoriteGroups
      .addMembership(groupId, {
        favoriteable_type: sourceableType,
        favoriteable_id: sourceableId,
      })
      .catch((e) => ({ error: e }));

    if (error) {
      dispatch({ type: "REPLACE_STATE", patch: { membershipIndex: prev.membershipIndex } });
      dispatch({ type: "DECREMENT_MEMBER_COUNT", groupId });
      globalThis.Qwoted?.showToast?.({ toastTitle: "Couldn't save source", variant: "danger" });
      return;
    }

    const membership = data?.data?.attributes || data;
    const realMembership = { id: membership?.id, saved_group_id: groupId, ...sourceMeta };
    const currentIndex = stateRef.current.membershipIndex;
    const currentMemberships = currentIndex[type]?.[id] || [];
    const finalIndex = {
      ...currentIndex,
      [type]: {
        ...currentIndex[type],
        [id]: currentMemberships.filter((m) => m.id !== optimisticMembership.id).concat(realMembership),
      },
    };
    dispatch({ type: "REPLACE_STATE", patch: { membershipIndex: finalIndex } });
  }, []);

  const removeFromGroup = useCallback(async (groupId, membershipId, sourceableType, sourceableId) => {
    // Until the add resolves, the membership only exists here. A DELETE addressed by its local id
    // cannot find a record, and the failure path below restores the membership index as it stood
    // before the request -- discarding the real id that arrived in the meantime and leaving the
    // source saved with nothing the UI can remove it by. Callers gate their own rows on
    // isOptimisticId; this is the guard for every caller at once.
    if (isOptimisticId(membershipId) || isOptimisticId(groupId)) return;

    const prev = stateRef.current;

    const type = sourceableType;
    const id = sourceableId;
    const prevMemberships = prev.membershipIndex[type]?.[id] || [];
    const newMemberships = prevMemberships.filter((m) => m.id !== membershipId);
    const newTypeIndex = { ...prev.membershipIndex[type] };
    if (newMemberships.length === 0) {
      delete newTypeIndex[id];
    } else {
      newTypeIndex[id] = newMemberships;
    }
    const newIndex = { ...prev.membershipIndex, [type]: newTypeIndex };
    dispatch({ type: "REPLACE_STATE", patch: { membershipIndex: newIndex } });
    dispatch({ type: "DECREMENT_MEMBER_COUNT", groupId });

    const { error } = await qapi.favoriteGroups.removeMembership(groupId, membershipId).catch((e) => ({ error: e }));
    if (error) {
      dispatch({ type: "REPLACE_STATE", patch: { membershipIndex: prev.membershipIndex } });
      dispatch({ type: "INCREMENT_MEMBER_COUNT", groupId });
      globalThis.Qwoted?.showToast?.({ toastTitle: "Couldn't remove source", variant: "danger" });
    }
  }, []);

  const updateGroup = useCallback(async (groupId, name) => {
    const prev = stateRef.current;
    dispatch({
      type: "REPLACE_STATE",
      patch: {
        groups: prev.groups
          .map((g) => (g.id === groupId ? { ...g, name } : g))
          .sort((a, b) => a.name.localeCompare(b.name)),
      },
    });

    const { data, error } = await qapi.favoriteGroups.updateGroup(groupId, { name }).catch((e) => ({ error: e }));

    if (error) {
      const original = prev.groups.find((g) => g.id === groupId);
      dispatch({
        type: "REPLACE_STATE",
        patch: {
          groups: stateRef.current.groups
            .map((g) => (g.id === groupId ? original || g : g))
            .sort((a, b) => a.name.localeCompare(b.name)),
        },
      });
      globalThis.Qwoted?.showToast?.({ toastTitle: "Couldn't rename group", variant: "danger" });
      return;
    }

    const updated = data?.data?.attributes || data;
    dispatch({
      type: "REPLACE_STATE",
      patch: {
        groups: stateRef.current.groups
          .map((g) => (g.id === groupId ? { ...g, ...updated } : g))
          .sort((a, b) => a.name.localeCompare(b.name)),
      },
    });
  }, []);

  const destroyGroup = useCallback(async (groupId) => {
    const prev = stateRef.current;

    const newGroups = prev.groups.filter((g) => g.id !== groupId);
    const newIndex = Object.fromEntries(
      Object.entries(prev.membershipIndex).map(([type, byId]) => [
        type,
        Object.fromEntries(
          Object.entries(byId)
            .map(([sid, memberships]) => [sid, memberships.filter((m) => m.saved_group_id !== groupId)])
            .filter(([, memberships]) => memberships.length > 0),
        ),
      ]),
    );
    dispatch({ type: "REPLACE_STATE", patch: { groups: newGroups, membershipIndex: newIndex } });

    const { error } = await qapi.favoriteGroups.destroyGroup(groupId).catch((e) => ({ error: e }));
    if (error) {
      dispatch({ type: "REPLACE_STATE", patch: { groups: prev.groups, membershipIndex: prev.membershipIndex } });
      globalThis.Qwoted?.showToast?.({ toastTitle: "Couldn't delete group", variant: "danger" });
    }
  }, []);

  const isSaved = useCallback((sourceableType, sourceableId) => {
    return (stateRef.current.membershipIndex[sourceableType]?.[sourceableId]?.length || 0) > 0;
  }, []);

  const membershipsFor = useCallback((sourceableType, sourceableId) => {
    return stateRef.current.membershipIndex[sourceableType]?.[sourceableId] || [];
  }, []);

  return {
    state,
    reload: load,
    createGroup,
    updateGroup,
    addToGroup,
    removeFromGroup,
    destroyGroup,
    isSaved,
    membershipsFor,
  };
}
